import type { Board } from '@game/models/Board';
import { getCell } from '@game/models/Board';
import type { Position } from '@game/types';
import { CENTER_POSITION } from '@game/constants/gameConstants';

export interface PlacedLetterPosition {
  letterId: string;
  position: Position;
  letter: string;
}

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validate that the first word passes through the center square (10, 10)
 */
export function validateFirstWord(positions: Position[]): ValidationResult {
  if (positions.length === 0) {
    return { valid: false, error: 'No letters placed' };
  }
  
  const passesCenter = positions.some(
    pos => pos.row === CENTER_POSITION.row && pos.col === CENTER_POSITION.col
  );
  
  if (!passesCenter) {
    return { 
      valid: false, 
      error: 'First word must pass through center square (marked with star)' 
    };
  }
  
  return { valid: true };
}

/**
 * Validate that placed letters connect to existing letters on the board
 */
export function validateConnection(
  positions: Position[],
  board: Board
): ValidationResult {
  if (positions.length === 0) {
    return { valid: false, error: 'No letters placed' };
  }
  
  // Check if any placed letter is adjacent to an existing letter
  let hasConnection = false;
  
  for (const pos of positions) {
    const adjacentPositions = [
      { row: pos.row - 1, col: pos.col }, // up
      { row: pos.row + 1, col: pos.col }, // down
      { row: pos.row, col: pos.col - 1 }, // left
      { row: pos.row, col: pos.col + 1 }, // right
    ];
    
    for (const adjPos of adjacentPositions) {
      const cell = getCell(board, adjPos);
      if (cell && cell.isOccupied) {
        // Make sure this occupied cell isn't one we just placed
        const isNewlyPlaced = positions.some(
          p => p.row === adjPos.row && p.col === adjPos.col
        );
        if (!isNewlyPlaced) {
          hasConnection = true;
          break;
        }
      }
    }
    
    if (hasConnection) break;
  }
  
  if (!hasConnection) {
    return { 
      valid: false, 
      error: 'Word must connect to existing letters on the board' 
    };
  }
  
  return { valid: true };
}

/**
 * Validate word continuity - no gaps and must be in a straight line
 */
export function validateContinuity(positions: Position[]): ValidationResult {
  if (positions.length === 0) {
    return { valid: false, error: 'No letters placed' };
  }
  
  if (positions.length === 1) {
    return { valid: true }; // Single letter is always valid
  }
  
  // Check if all letters are in the same row or same column
  const allSameRow = positions.every(pos => pos.row === positions[0].row);
  const allSameCol = positions.every(pos => pos.col === positions[0].col);
  
  if (!allSameRow && !allSameCol) {
    return { 
      valid: false, 
      error: 'Letters must be placed in a straight line (horizontal or vertical)' 
    };
  }
  
  // Check for gaps
  if (allSameRow) {
    // Horizontal word - check column continuity
    const cols = positions.map(pos => pos.col).sort((a, b) => a - b);
    for (let i = 1; i < cols.length; i++) {
      if (cols[i] - cols[i - 1] > 1) {
        return { 
          valid: false, 
          error: 'Letters cannot have gaps between them' 
        };
      }
    }
  } else {
    // Vertical word - check row continuity
    const rows = positions.map(pos => pos.row).sort((a, b) => a - b);
    for (let i = 1; i < rows.length; i++) {
      if (rows[i] - rows[i - 1] > 1) {
        return { 
          valid: false, 
          error: 'Letters cannot have gaps between them' 
        };
      }
    }
  }
  
  return { valid: true };
}

/**
 * Validate word placement considering existing letters
 * This checks that the word + existing letters on the board form a continuous line
 */
export function validateContinuityWithBoard(
  positions: Position[],
  board: Board
): ValidationResult {
  if (positions.length === 0) {
    return { valid: false, error: 'No letters placed' };
  }
  
  if (positions.length === 1) {
    return { valid: true }; // Single letter with existing letters is always valid
  }
  
  // Check if all letters are in the same row or same column
  const allSameRow = positions.every(pos => pos.row === positions[0].row);
  const allSameCol = positions.every(pos => pos.col === positions[0].col);
  
  if (!allSameRow && !allSameCol) {
    return { 
      valid: false, 
      error: 'Letters must be placed in a straight line (horizontal or vertical)' 
    };
  }
  
  // Now check for gaps considering existing letters on the board
  if (allSameRow) {
    // Horizontal word
    const row = positions[0].row;
    const cols = positions.map(pos => pos.col).sort((a, b) => a - b);
    const minCol = cols[0];
    const maxCol = cols[cols.length - 1];
    
    // Check all cells between min and max (including existing letters)
    for (let col = minCol; col <= maxCol; col++) {
      const cell = getCell(board, { row, col });
      const isNewlyPlaced = positions.some(p => p.row === row && p.col === col);
      
      if (!cell) continue;
      
      // If this cell is neither newly placed nor already occupied, we have a gap
      if (!isNewlyPlaced && !cell.isOccupied) {
        return { 
          valid: false, 
          error: 'Word cannot have gaps (must account for existing letters)' 
        };
      }
    }
  } else {
    // Vertical word
    const col = positions[0].col;
    const rows = positions.map(pos => pos.row).sort((a, b) => a - b);
    const minRow = rows[0];
    const maxRow = rows[rows.length - 1];
    
    // Check all cells between min and max (including existing letters)
    for (let row = minRow; row <= maxRow; row++) {
      const cell = getCell(board, { row, col });
      const isNewlyPlaced = positions.some(p => p.row === row && p.col === col);
      
      if (!cell) continue;
      
      // If this cell is neither newly placed nor already occupied, we have a gap
      if (!isNewlyPlaced && !cell.isOccupied) {
        return { 
          valid: false, 
          error: 'Word cannot have gaps (must account for existing letters)' 
        };
      }
    }
  }
  
  return { valid: true };
}

/**
 * Main validation function - validates all placement rules
 */
export function validatePlacement(
  placedLetters: PlacedLetterPosition[],
  board: Board
): ValidationResult {
  if (placedLetters.length === 0) {
    return { valid: false, error: 'No letters placed' };
  }
  
  const positions = placedLetters.map(pl => pl.position);
  
  // Check continuity first (basic rule that applies to all scenarios)
  const continuityResult = validateContinuityWithBoard(positions, board);
  if (!continuityResult.valid) {
    return continuityResult;
  }
  
  // Check if this is the first word on the board
  if (board.isEmpty) {
    // First word must pass through center
    return validateFirstWord(positions);
  } else {
    // Subsequent words must connect to existing letters
    return validateConnection(positions, board);
  }
}

/**
 * Get the word string formed by placed letters (including existing letters on board)
 * Returns the complete word including any existing letters that fill gaps AND adjacent letters
 */
export function getFormedWord(
  placedLetters: PlacedLetterPosition[],
  board: Board
): string {
  if (placedLetters.length === 0) return '';
  
  const positions = placedLetters.map(pl => pl.position);
  
  // Special case: when placing a single letter, check both directions
  // and pick the one that forms a word with existing letters
  if (positions.length === 1) {
    const pos = positions[0];
    
    // Try horizontal
    const horizontalWord = extractWordHorizontally(pos.row, pos.col, pos.col, placedLetters, board);
    
    // Try vertical
    const verticalWord = extractWordVertically(pos.row, pos.row, pos.col, placedLetters, board);
    
    // Return the longer word (the one that connects to existing letters)
    if (horizontalWord.length > verticalWord.length) {
      return horizontalWord;
    } else if (verticalWord.length > horizontalWord.length) {
      return verticalWord;
    } else {
      // Both same length (probably both length 1, isolated letter) - return horizontal by default
      return horizontalWord;
    }
  }
  
  // Multiple letters placed - determine direction normally
  const allSameRow = positions.every(pos => pos.row === positions[0].row);
  
  if (allSameRow) {
    // Horizontal word
    const row = positions[0].row;
    const cols = positions.map(pos => pos.col).sort((a, b) => a - b);
    return extractWordHorizontally(row, cols[0], cols[cols.length - 1], placedLetters, board);
  } else {
    // Vertical word
    const col = positions[0].col;
    const rows = positions.map(pos => pos.row).sort((a, b) => a - b);
    return extractWordVertically(rows[0], rows[rows.length - 1], col, placedLetters, board);
  }
}

/**
 * Extract word horizontally, extending to include adjacent existing letters
 */
function extractWordHorizontally(
  row: number,
  minCol: number,
  maxCol: number,
  placedLetters: PlacedLetterPosition[],
  board: Board
): string {
  // Extend left to include adjacent existing letters
  while (minCol > 0) {
    const cell = getCell(board, { row, col: minCol - 1 });
    if (!cell || !cell.isOccupied) break;
    minCol--;
  }
  
  // Extend right to include adjacent existing letters
  while (maxCol < 20) { // Assuming 21x21 board (0-20)
    const cell = getCell(board, { row, col: maxCol + 1 });
    if (!cell || !cell.isOccupied) break;
    maxCol++;
  }
  
  let word = '';
  for (let col = minCol; col <= maxCol; col++) {
    const placed = placedLetters.find(pl => pl.position.row === row && pl.position.col === col);
    if (placed) {
      word += placed.letter;
    } else {
      const cell = getCell(board, { row, col });
      if (cell && cell.letter) {
        word += cell.letter.character;
      }
    }
  }
  return word;
}

/**
 * Extract word vertically, extending to include adjacent existing letters
 */
function extractWordVertically(
  minRow: number,
  maxRow: number,
  col: number,
  placedLetters: PlacedLetterPosition[],
  board: Board
): string {
  // Extend up to include adjacent existing letters
  while (minRow > 0) {
    const cell = getCell(board, { row: minRow - 1, col });
    if (!cell || !cell.isOccupied) break;
    minRow--;
  }
  
  // Extend down to include adjacent existing letters
  while (maxRow < 20) { // Assuming 21x21 board (0-20)
    const cell = getCell(board, { row: maxRow + 1, col });
    if (!cell || !cell.isOccupied) break;
    maxRow++;
  }
  
  let word = '';
  for (let row = minRow; row <= maxRow; row++) {
    const placed = placedLetters.find(pl => pl.position.row === row && pl.position.col === col);
    if (placed) {
      word += placed.letter;
    } else {
      const cell = getCell(board, { row, col });
      if (cell && cell.letter) {
        word += cell.letter.character;
      }
    }
  }
  return word;
}

/**
 * Get all letters and positions in the formed word (including existing letters on board)
 * This is used for scoring - we need to score ALL letters in the word
 * 
 * Note: For newly placed letters, this returns a placeholder. The caller should replace
 * these with actual Letter objects from the player's hand.
 */
export function getCompleteWordForScoring(
  placedLetters: PlacedLetterPosition[],
  board: Board
): { letterCharacters: string[]; positions: Position[]; newlyPlacedPositions: Set<string> } {
  if (placedLetters.length === 0) {
    return { letterCharacters: [], positions: [], newlyPlacedPositions: new Set() };
  }
  
  const positions = placedLetters.map(pl => pl.position);
  const newlyPlacedPositions = new Set<string>();
  
  // Mark newly placed positions
  placedLetters.forEach(pl => {
    newlyPlacedPositions.add(`${pl.position.row},${pl.position.col}`);
  });
  
  // Special case: when placing a single letter, check both directions
  // and pick the one that forms a word with existing letters
  if (positions.length === 1) {
    const pos = positions[0];
    
    // Try horizontal
    const horizontalResult = extractCompleteWordHorizontally(pos.row, pos.col, pos.col, placedLetters, board, newlyPlacedPositions);
    
    // Try vertical
    const verticalResult = extractCompleteWordVertically(pos.row, pos.row, pos.col, placedLetters, board, newlyPlacedPositions);
    
    // Return the longer word (the one that connects to existing letters)
    if (horizontalResult.positions.length > verticalResult.positions.length) {
      return horizontalResult;
    } else if (verticalResult.positions.length > horizontalResult.positions.length) {
      return verticalResult;
    } else {
      // Both same length - return horizontal by default
      return horizontalResult;
    }
  }
  
  // Multiple letters placed - determine direction normally
  const allSameRow = positions.every(pos => pos.row === positions[0].row);
  
  if (allSameRow) {
    // Horizontal word
    const row = positions[0].row;
    const cols = positions.map(pos => pos.col).sort((a, b) => a - b);
    return extractCompleteWordHorizontally(row, cols[0], cols[cols.length - 1], placedLetters, board, newlyPlacedPositions);
  } else {
    // Vertical word
    const col = positions[0].col;
    const rows = positions.map(pos => pos.row).sort((a, b) => a - b);
    return extractCompleteWordVertically(rows[0], rows[rows.length - 1], col, placedLetters, board, newlyPlacedPositions);
  }
}

/**
 * Extract complete word horizontally for scoring
 */
function extractCompleteWordHorizontally(
  row: number,
  minCol: number,
  maxCol: number,
  placedLetters: PlacedLetterPosition[],
  board: Board,
  newlyPlacedPositions: Set<string>
): { letterCharacters: string[]; positions: Position[]; newlyPlacedPositions: Set<string> } {
  const letterCharacters: string[] = [];
  const allPositions: Position[] = [];
  
  // Extend left to include adjacent existing letters
  while (minCol > 0) {
    const cell = getCell(board, { row, col: minCol - 1 });
    if (!cell || !cell.isOccupied) break;
    minCol--;
  }
  
  // Extend right to include adjacent existing letters
  while (maxCol < 20) { // Assuming 21x21 board (0-20)
    const cell = getCell(board, { row, col: maxCol + 1 });
    if (!cell || !cell.isOccupied) break;
    maxCol++;
  }
  
  for (let col = minCol; col <= maxCol; col++) {
    const placed = placedLetters.find(pl => pl.position.row === row && pl.position.col === col);
    if (placed) {
      // Newly placed letter
      letterCharacters.push(placed.letter);
    } else {
      // Existing letter on board
      const cell = getCell(board, { row, col });
      if (cell && cell.letter) {
        letterCharacters.push(cell.letter.character);
      }
    }
    allPositions.push({ row, col });
  }
  
  return { letterCharacters, positions: allPositions, newlyPlacedPositions };
}

/**
 * Extract complete word vertically for scoring
 */
function extractCompleteWordVertically(
  minRow: number,
  maxRow: number,
  col: number,
  placedLetters: PlacedLetterPosition[],
  board: Board,
  newlyPlacedPositions: Set<string>
): { letterCharacters: string[]; positions: Position[]; newlyPlacedPositions: Set<string> } {
  const letterCharacters: string[] = [];
  const allPositions: Position[] = [];
  
  // Extend up to include adjacent existing letters
  while (minRow > 0) {
    const cell = getCell(board, { row: minRow - 1, col });
    if (!cell || !cell.isOccupied) break;
    minRow--;
  }
  
  // Extend down to include adjacent existing letters
  while (maxRow < 20) { // Assuming 21x21 board (0-20)
    const cell = getCell(board, { row: maxRow + 1, col });
    if (!cell || !cell.isOccupied) break;
    maxRow++;
  }
  
  for (let row = minRow; row <= maxRow; row++) {
    const placed = placedLetters.find(pl => pl.position.row === row && pl.position.col === col);
    if (placed) {
      // Newly placed letter
      letterCharacters.push(placed.letter);
    } else {
      // Existing letter on board
      const cell = getCell(board, { row, col });
      if (cell && cell.letter) {
        letterCharacters.push(cell.letter.character);
      }
    }
    allPositions.push({ row, col });
  }
  
  return { letterCharacters, positions: allPositions, newlyPlacedPositions };
}

/**
 * Crossword information for a single perpendicular word formed
 */
export interface CrosswordInfo {
  word: string;
  positions: Position[];
  letters: string[]; // All letter characters in the crossword
  newlyPlacedPositions: Set<string>; // Which positions in this crossword are newly placed
}

/**
 * Get all crosswords (perpendicular words) formed by the placed letters
 * In Scrabble, when you place letters that touch existing letters perpendicularly,
 * those perpendicular words also count toward your score.
 * 
 * @param placedLetters - The letters being placed this turn
 * @param board - The game board
 * @returns Array of crossword information
 */
export function getCrosswords(
  placedLetters: PlacedLetterPosition[],
  board: Board
): CrosswordInfo[] {
  if (placedLetters.length === 0) return [];
  
  const crosswords: CrosswordInfo[] = [];
  const positions = placedLetters.map(pl => pl.position);
  
  // Determine main word direction
  let isMainWordHorizontal: boolean;
  
  if (positions.length === 1) {
    // For single letter, check both directions to determine actual main word direction
    const pos = positions[0];
    const horizontalWord = extractWordHorizontally(pos.row, pos.col, pos.col, placedLetters, board);
    const verticalWord = extractWordVertically(pos.row, pos.row, pos.col, placedLetters, board);
    
    // Main word is the longer one
    isMainWordHorizontal = horizontalWord.length >= verticalWord.length;
  } else {
    // Multiple letters - determine by checking if all in same row
    const allSameRow = positions.every(pos => pos.row === positions[0].row);
    isMainWordHorizontal = allSameRow;
  }
  
  // For each placed letter, check if it forms a perpendicular word
  for (const placedLetter of placedLetters) {
    const pos = placedLetter.position;
    
    if (isMainWordHorizontal) {
      // Main word is horizontal, check for vertical crosswords
      const crossword = extractVerticalWord(pos, board, placedLetter);
      if (crossword && crossword.positions.length > 1) {
        // Only count if it forms a word with existing letters (length > 1)
        crosswords.push(crossword);
      }
    } else {
      // Main word is vertical, check for horizontal crosswords
      const crossword = extractHorizontalWord(pos, board, placedLetter);
      if (crossword && crossword.positions.length > 1) {
        crosswords.push(crossword);
      }
    }
  }
  
  return crosswords;
}

/**
 * Extract a vertical word at a given position, including adjacent existing letters
 */
function extractVerticalWord(
  pos: Position,
  board: Board,
  placedLetter: PlacedLetterPosition
): CrosswordInfo | null {
  const { row, col } = pos;
  
  // Find the topmost letter (could be existing or the placed one)
  let minRow = row;
  while (minRow > 0) {
    const cell = getCell(board, { row: minRow - 1, col });
    if (!cell || !cell.isOccupied) break;
    minRow--;
  }
  
  // Find the bottommost letter
  let maxRow = row;
  while (maxRow < 20) { // Assuming 21x21 board (0-20)
    const cell = getCell(board, { row: maxRow + 1, col });
    if (!cell || !cell.isOccupied) break;
    maxRow++;
  }
  
  // If it's just the placed letter alone (no adjacent letters), no crossword
  if (minRow === row && maxRow === row) {
    return null;
  }
  
  // Build the word
  const positions: Position[] = [];
  const letters: string[] = [];
  const newlyPlacedPositions = new Set<string>();
  let word = '';
  
  for (let r = minRow; r <= maxRow; r++) {
    const currentPos = { row: r, col };
    positions.push(currentPos);
    
    if (r === row) {
      // This is the newly placed letter
      word += placedLetter.letter;
      letters.push(placedLetter.letter);
      newlyPlacedPositions.add(`${r},${col}`);
    } else {
      // Existing letter on board
      const cell = getCell(board, currentPos);
      if (cell && cell.letter) {
        word += cell.letter.character;
        letters.push(cell.letter.character);
      }
    }
  }
  
  return {
    word,
    positions,
    letters,
    newlyPlacedPositions,
  };
}

/**
 * Extract a horizontal word at a given position, including adjacent existing letters
 */
function extractHorizontalWord(
  pos: Position,
  board: Board,
  placedLetter: PlacedLetterPosition
): CrosswordInfo | null {
  const { row, col } = pos;
  
  // Find the leftmost letter
  let minCol = col;
  while (minCol > 0) {
    const cell = getCell(board, { row, col: minCol - 1 });
    if (!cell || !cell.isOccupied) break;
    minCol--;
  }
  
  // Find the rightmost letter
  let maxCol = col;
  while (maxCol < 20) { // Assuming 21x21 board (0-20)
    const cell = getCell(board, { row, col: maxCol + 1 });
    if (!cell || !cell.isOccupied) break;
    maxCol++;
  }
  
  // If it's just the placed letter alone (no adjacent letters), no crossword
  if (minCol === col && maxCol === col) {
    return null;
  }
  
  // Build the word
  const positions: Position[] = [];
  const letters: string[] = [];
  const newlyPlacedPositions = new Set<string>();
  let word = '';
  
  for (let c = minCol; c <= maxCol; c++) {
    const currentPos = { row, col: c };
    positions.push(currentPos);
    
    if (c === col) {
      // This is the newly placed letter
      word += placedLetter.letter;
      letters.push(placedLetter.letter);
      newlyPlacedPositions.add(`${row},${c}`);
    } else {
      // Existing letter on board
      const cell = getCell(board, currentPos);
      if (cell && cell.letter) {
        word += cell.letter.character;
        letters.push(cell.letter.character);
      }
    }
  }
  
  return {
    word,
    positions,
    letters,
    newlyPlacedPositions,
  };
}
